/**
 * RevenueFormModal — фиксация поступления денег (cash inflow / ДДС-приход).
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createRevenue, updateRevenue,
  fetchPaymentAccounts, fetchObjects,
  PAYMENT_LABELS, REVENUE_TYPE_LABELS,
  type RevenueRow, type RevenuePayload, type PaymentMethod, type RevenueType,
} from "@/api/finance";
import { ApiError } from "@/api/client";


interface Props {
  open: boolean;
  initial?: RevenueRow | null;
  onClose: () => void;
}


function todayIso(): string {
  const d = new Date(Date.now() + 3 * 60 * 60 * 1000);  // МСК
  return d.toISOString().slice(0, 10);
}


export function RevenueFormModal({ open, initial, onClose }: Props) {
  const isEdit = !!initial;
  const qc = useQueryClient();
  const [date, setDate] = useState(todayIso());
  const [counterparty, setCounterparty] = useState("");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<PaymentMethod>("bank_transfer");
  const [accountId, setAccountId] = useState<number | "">("");
  const [type, setType] = useState<RevenueType>("prepay");
  const [region, setRegion] = useState<"" | "Москва" | "Юг">("");
  const [objectId, setObjectId] = useState<number | "">("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const accountsQ = useQuery({
    queryKey: ["finance-accounts"],
    queryFn: () => fetchPaymentAccounts(),
    enabled: open,
  });
  const objectsQ = useQuery({
    queryKey: ["finance-objects-all"],
    queryFn: () => fetchObjects(),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (initial) {
      setDate(initial.received_at);
      setCounterparty(initial.counterparty || "");
      setAmount(String(Math.abs(initial.amount)));
      setMethod(initial.payment_method);
      setAccountId(initial.account_id ?? "");
      setType(initial.type);
      setRegion((initial.region as "" | "Москва" | "Юг") || "");
      setObjectId(initial.object_id ?? "");
      setNote(initial.note || "");
    } else {
      setDate(todayIso());
      setCounterparty(""); setAmount("");
      setMethod("bank_transfer"); setAccountId("");
      setType("prepay"); setRegion(""); setObjectId(""); setNote("");
    }
  }, [open, initial]);

  const mut = useMutation({
    mutationFn: async () => {
      const amt = parseFloat(amount.replace(",", "."));
      if (!date) throw new Error("Дата обязательна");
      if (!isFinite(amt) || amt <= 0) throw new Error("Сумма должна быть больше нуля");
      const payload: RevenuePayload = {
        received_at: date,
        amount: amt,
        type,
        counterparty: counterparty.trim() || null,
        payment_method: method,
        account_id: accountId === "" ? null : Number(accountId),
        object_id: objectId === "" ? null : Number(objectId),
        region: region || null,
        note: note.trim() || null,
      };
      return isEdit ? updateRevenue(initial!.id, payload) : createRevenue(payload);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["finance-revenue"] });
      void qc.invalidateQueries({ queryKey: ["finance-overview"] });
      void qc.invalidateQueries({ queryKey: ["finance-objects"] });
      onClose();
    },
    onError: (e) => {
      const msg = e instanceof ApiError
        ? ((e.body as { detail?: string })?.detail || `HTTP ${e.status}`)
        : (e as Error).message;
      setError(msg);
    },
  });

  if (!open) return null;
  const accounts = accountsQ.data?.items || [];
  const objects = objectsQ.data || [];

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "grid", placeItems: "center", zIndex: 100, padding: 16 }}
    >
      <div className="card" style={{ maxWidth: 520, width: "100%", maxHeight: "90vh", overflowY: "auto", padding: 24, position: "relative" }}>
        <button
          type="button" onClick={onClose} aria-label="Закрыть"
          style={{ position: "absolute", top: 12, right: 12, border: "none", background: "transparent", color: "var(--mute)", cursor: "pointer", fontSize: 20, padding: 4 }}
        >×</button>

        <h3 style={{ fontFamily: "var(--serif)", fontSize: 22, fontWeight: 700, marginBottom: 20, color: "var(--ink)" }}>
          {isEdit ? "Редактировать поступление" : "Новое поступление"}
        </h3>

        <form onSubmit={(e) => { e.preventDefault(); mut.mutate(); }}
              style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div className="row row-2">
            <label style={fieldStyle}>
              Дата
              <input type="date" className="input" required value={date}
                     onChange={e => setDate(e.target.value)} max={todayIso()}/>
            </label>
            <label style={fieldStyle}>
              Сумма, ₽
              <input type="text" inputMode="decimal" className="input" required value={amount}
                     onChange={e => setAmount(e.target.value.replace(/[^\d.,]/g, ""))} placeholder="100000"/>
            </label>
          </div>

          <label style={fieldStyle}>
            Контрагент / клиент
            <input type="text" className="input" value={counterparty}
                   onChange={e => setCounterparty(e.target.value)} placeholder="Иванов"/>
          </label>

          <div className="row row-2">
            <label style={fieldStyle}>
              Тип
              <select className="input" value={type} onChange={e => setType(e.target.value as RevenueType)}>
                {(Object.keys(REVENUE_TYPE_LABELS) as RevenueType[]).map(t => (
                  <option key={t} value={t}>{REVENUE_TYPE_LABELS[t]}</option>
                ))}
              </select>
            </label>
            <label style={fieldStyle}>
              Способ оплаты
              <select className="input" value={method} onChange={e => setMethod(e.target.value as PaymentMethod)}>
                {(Object.keys(PAYMENT_LABELS) as PaymentMethod[]).map(m => (
                  <option key={m} value={m}>{PAYMENT_LABELS[m]}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="row row-2">
            <label style={fieldStyle}>
              Счёт / касса
              <select className="input" value={accountId === "" ? "" : String(accountId)}
                      onChange={e => setAccountId(e.target.value ? parseInt(e.target.value, 10) : "")}>
                <option value="">— не указан —</option>
                {accounts.map(a => (
                  <option key={a.id} value={a.id}>{a.name}{a.bank_label ? ` (${a.bank_label})` : ""}</option>
                ))}
              </select>
            </label>
            <label style={fieldStyle}>
              Регион
              <select className="input" value={region} onChange={e => setRegion(e.target.value as "Москва" | "Юг" | "")}>
                <option value="">— не указан —</option>
                <option value="Москва">Москва</option>
                <option value="Юг">Юг</option>
              </select>
            </label>
          </div>

          <label style={fieldStyle}>
            Объект (опционально)
            <select className="input" value={objectId === "" ? "" : String(objectId)}
                    onChange={e => setObjectId(e.target.value ? parseInt(e.target.value, 10) : "")}>
              <option value="">— не привязан —</option>
              {objects.map(o => (
                <option key={o.id} value={o.id}>
                  {o.client_name}{o.period_label ? ` · ${o.period_label}` : ""}
                </option>
              ))}
            </select>
          </label>

          <label style={fieldStyle}>
            Заметка
            <textarea className="input" rows={2} value={note}
                      onChange={e => setNote(e.target.value)}
                      style={{ resize: "vertical", fontFamily: "var(--sans)" }}/>
          </label>

          {error && (
            <div className="alert alert-red"><div className="alert-body"><div>{error}</div></div></div>
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <button type="button" className="btn" onClick={onClose} disabled={mut.isPending}>Отмена</button>
            <button type="submit" className="btn btn-primary" disabled={mut.isPending}>
              {mut.isPending ? "Сохранение…" : (isEdit ? "Сохранить" : "Зафиксировать")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


const fieldStyle: React.CSSProperties = {
  display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--ink-3)",
};
